import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dumbbell, Footprints, Utensils, Moon, LineChart, ClipboardCheck, Scale, Sparkles, ArrowRight, Lock, TrendingUp } from "lucide-react";
import BuildPhaseEducationCard from "@/components/build/BuildPhaseEducationCard";

import { RebuildScoreCard, EatCheckinButton } from "@/components/RebuildScoreCard";

const PHASE_META = {
  BASELINE:    { color: "#E65322", label: "BASELINE",    weeks: [1, 2, 3] },
  BUILD:       { color: "#2A6FDB", label: "BUILD",       weeks: [4, 5, 6] },
  OVERLOAD:    { color: "#2F8F4E", label: "OVERLOAD",    weeks: [7, 8, 9] },
  CONSOLIDATE: { color: "#7A4CC2", label: "CONSOLIDATE", weeks: [10, 11, 12] },
};

const BW_TREND_LABEL = {
  DOWN:              "Weight trend: DOWN",
  STABLE:            "Weight trend: STABLE",
  MODESTLY_UP:       "Weight trend: MODESTLY UP",
  RAPIDLY_UP:        "Weight trend: RAPIDLY UP",
  INSUFFICIENT_DATA: "Weight trend: INSUFFICIENT DATA",
};

export default function BuildDashboard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await api.get("/build/dashboard");
        if (ok) setState(data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load BUILD"); }
      finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, []);

  const weekArr = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  if (loading) return <div className="text-sm text-muted-foreground">Loading BUILD…</div>;

  if (!state?.active) {
    return (
      <div className="space-y-8">
        <div>
          <div className="label-eyebrow">Pathway</div>
          <h1 className="wordmark mt-3 text-5xl md:text-7xl uppercase leading-[0.9]">BA30 <span className="text-primary">BUILD.</span></h1>
          <p className="mt-3 text-foreground/70 max-w-2xl">Build measurable muscle without making fitness your whole life. Twelve weeks of guided hypertrophy — reps, load, selective volume, in that order.</p>
        </div>
        <div className="bg-foreground text-background rounded-lg p-8 md:p-10 max-w-2xl">
          <div className="label-eyebrow !text-background/70">Start BUILD Block</div>
          <p className="mt-3 font-head uppercase text-2xl md:text-3xl leading-snug">
            Three sessions a week. High protein. <span className="text-primary">Progress what you earn.</span> Recover properly. Stable steps.
          </p>
          <p className="mt-4 text-sm text-background/70">Set up your baseline in under two minutes. You can change nutrition mode later without resetting the block.</p>
          <Link to="/app/build/start" className="btn-primary mt-8 inline-flex" data-testid="build-start-cta">
            Start BUILD pathway <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </div>
    );
  }

  const { week, phase, phase_color, phase_purpose, track, curriculum, targets, train, walk, eat, recover, bodyweight, review_status, next_action, completed_weeks } = state;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-testid="build-header">
        <div>
          <div className="label-eyebrow">BA30 BUILD</div>
          <h1 className="wordmark mt-2 text-4xl md:text-6xl uppercase leading-[0.9]">Week <span style={{ color: phase_color }}>{week}.</span></h1>
          <div className="mt-3 inline-flex items-center gap-2 rounded-md px-3 py-1.5 font-head uppercase tracking-widest text-xs text-white" style={{ background: phase_color }} data-testid="build-phase-badge">
            {phase} · {track}
          </div>
          <p className="mt-4 text-sm text-foreground/75 max-w-xl">{phase_purpose}</p>
        </div>
        <button onClick={() => nav(`/app/build/week/${week}`)}
          className="btn-primary" data-testid="build-next-action">
          {next_action}
        </button>
      </div>

      {/* This week's goal */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="build-goal">
        <div className="label-eyebrow" style={{ color: phase_color }}>This Week's Goal</div>
        <h3 className="mt-3 font-head uppercase text-xl md:text-2xl">{curriculum.title}.</h3>
        <p className="mt-3 text-sm text-foreground/80 max-w-3xl">{curriculum.lesson}</p>
      </div>

      {/* 4-pillar grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Block icon={Dumbbell} label="Train" testid="build-block-train"
          v1={`${train.completed} / ${train.target}`} sub={`Trend: ${train.strength_trend}`} />
        <Block icon={Footprints} label="Walk" testid="build-block-walk"
          v1={walk.current_step_target?.toLocaleString()}
          sub={walk.weekly_average ? `Avg ${walk.weekly_average.toLocaleString()}/day` : "MAINTAIN"} />
        <Block icon={Utensils} label="Eat" testid="build-block-eat"
          v1={eat.mode} sub={eat.mode === "PRECISION" && eat.calorie_target ? `${eat.calorie_target} kcal · ${eat.protein_target_g}g protein` : `${eat.protein_target_g}g protein`} />
        <Block icon={Moon} label="Recover" testid="build-block-recover"
          v1={recover.readiness} sub={recover.latest ? `Sleep ${recover.latest.sleep}` : "Log today"}
          accent={recover.readiness === "GREEN" ? "text-emerald-500" : recover.readiness === "AMBER" ? "text-amber-500" : "text-red-500"} />
      </div>

      <BuildPhaseEducationCard />

      {/* WALK stable-baseline card */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="build-walk-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Footprints size={12} /> Today's Progress</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="wordmark text-4xl" data-testid="build-walk-today">{walk.today_steps?.toLocaleString() ?? 0}</div>
              <div className="wordmark text-2xl text-muted-foreground">/ {walk.today_target?.toLocaleString()}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground ml-1">steps</div>
            </div>
            <div className="mt-3 h-2 w-full max-w-md rounded-full bg-muted overflow-hidden">
              <div className="h-full transition-all"
                style={{ width: `${walk.today_progress_pct || 0}%`, background: walk.today_progress_pct >= 100 ? "#2F8F4E" : phase_color }} />
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">
              {walk.today_progress_pct || 0}% of daily target
              {walk.weekly_average != null && <> · Avg {walk.weekly_average.toLocaleString()}/day this week</>}
            </div>
          </div>
          <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-head uppercase tracking-widest ${
            walk.walk_status === "KEEP" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/30" :
            "bg-amber-500/10 text-amber-500 border border-amber-500/30"
          }`} data-testid="build-walk-status">
            {walk.walk_status === "HOLD_RECOVER" ? "HOLD · RECOVER" : "MAINTAIN"}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <div className="label-eyebrow">Current Step Target · {walk.current_step_target?.toLocaleString()} / day</div>
          <p className="mt-3 text-sm text-foreground/80 max-w-3xl leading-relaxed" data-testid="build-walk-message">{walk.walk_status_message}</p>
        </div>
        <button onClick={() => nav("/app/move")} className="mt-4 btn-primary btn-sm" data-testid="build-walk-log-cta">
          Log Today's Steps
        </button>
        <details className="mt-4 group">
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground select-none">
            Why isn't my step target increasing?
          </summary>
          <p className="mt-2 text-xs text-foreground/70 leading-relaxed max-w-3xl">{walk.explainer}</p>
        </details>
      </div>

      {/* Weight + Rebuild Score row */}
      <div className="grid md:grid-cols-2 gap-4">
        <RebuildScoreCard score={state.rebuild_score_v2} />
        <div className="bg-card border border-border rounded-lg p-6" data-testid="build-block-weight">
          <div className="flex items-center gap-2 label-eyebrow"><Scale size={12} /> Bodyweight Trend</div>
          <div className="mt-3 flex items-baseline gap-4">
            <div>
              <div className="wordmark text-4xl">{bodyweight.rolling_7day ?? bodyweight.starting} <span className="text-lg text-muted-foreground">kg</span></div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">7-day average</div>
            </div>
            {bodyweight.rolling_7day && (
              <div className="text-sm text-muted-foreground">
                from <span className="text-foreground">{bodyweight.starting} kg</span> starting
              </div>
            )}
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[10px] font-head uppercase tracking-widest">
            <TrendingUp size={10} /> {BW_TREND_LABEL[bodyweight.trend_state] || bodyweight.trend_state}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">A modest increase can be appropriate. Performance and recovery matter more than the scale.</p>
          <div className="mt-4">
            <EatCheckinButton pathway="BUILD" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 md:col-span-2" data-testid="build-block-review">
          <div className="flex items-center gap-2 label-eyebrow"><ClipboardCheck size={12} /> Weekly Review</div>
          <div className="mt-3 font-head uppercase text-2xl">{review_status === "due" ? "DUE THIS WEEK" : "COMPLETE"}</div>
          <p className="mt-3 text-sm text-foreground/75">{curriculum.review}</p>
          <button onClick={() => nav(`/app/build/week/${week}`)}
            className="mt-4 text-sm underline text-muted-foreground hover:text-foreground" data-testid="build-open-week">
            Open Week {week} →
          </button>
        </div>
      </div>

      {/* Week strip */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="build-weeks">
        <div className="flex items-center gap-2 label-eyebrow"><LineChart size={12} /> 12-Week Path</div>
        <div className="mt-4 grid grid-cols-6 md:grid-cols-12 gap-2">
          {weekArr.map((w) => {
            const p = Object.entries(PHASE_META).find(([, m]) => m.weeks.includes(w))[0];
            const meta = PHASE_META[p];
            const active = w === week;
            const done = (completed_weeks || []).includes(w);
            const locked = w > week;
            return (
              <button key={w} disabled={locked} onClick={() => nav(`/app/build/week/${w}`)}
                className={`aspect-square rounded-md text-xs font-head uppercase tracking-wider flex flex-col items-center justify-center transition-all
                  ${active ? "text-white ring-2 ring-offset-1 ring-offset-background" : locked ? "bg-muted text-muted-foreground/40 cursor-not-allowed" : done ? "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20" : "bg-muted hover:bg-muted/70"}`}
                style={active ? { background: meta.color, "--tw-ring-color": meta.color } : {}}
                data-testid={`build-week-tile-${w}`}>
                {locked ? <Lock size={10} /> : <span className="text-[10px] opacity-70">W</span>}
                <span className="text-lg leading-none">{w}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-[10px] uppercase tracking-widest">
          {Object.entries(PHASE_META).map(([k, m]) => (
            <div key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: m.color }} /> {k} · W{m.weeks[0]}–{m.weeks[m.weeks.length - 1]}</div>
          ))}
        </div>
      </div>

      {/* Positioning strip */}
      <div className="bg-foreground text-background rounded-lg p-6 flex items-start gap-4" data-testid="build-positioning">
        <Sparkles className="text-primary shrink-0 mt-1" size={20} />
        <div>
          <div className="font-head uppercase text-lg">Build muscle without making fitness your whole life.</div>
          <p className="mt-2 text-sm text-background/70">Three sessions a week. High protein. Progress reps → load → selective volume, in that order. Steps stay stable. Recover properly.</p>
        </div>
      </div>
    </div>
  );
}

function Block({ icon: Icon, label, v1, sub, accent, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow"><Icon size={12} /> {label}</div>
      <div className={`wordmark mt-3 text-3xl ${accent || ""}`}>{v1 ?? "—"}</div>
      <p className="mt-2 text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}
